import { CURRENT_CLIENT } from '@/constants'
import { flattenThemeColors } from '@/lib/theme-colors-normalize'

export const IDENTICON_VARIANT = 'marble' as const
export const IDENTICON_FALLBACK_BRAND_COLOR = '#7b7066'

export type IdenticonVariant = typeof IDENTICON_VARIANT

export interface IdenticonRenderProps {
  name: string
  variant: IdenticonVariant
  colors: string[]
  square: boolean
  size: number
  title: boolean
}

interface HslColor {
  h: number
  s: number
  l: number
}

function normalizeHue(hue: number): number {
  return ((hue % 360) + 360) % 360
}

function parseHexColor(color: string): { r: number; g: number; b: number } | null {
  const normalized = color.trim().replace(/^#/, '')
  const hex =
    normalized.length === 3
      ? normalized
          .split('')
          .map((char) => `${char}${char}`)
          .join('')
      : normalized

  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null

  return {
    r: Number.parseInt(hex.slice(0, 2), 16),
    g: Number.parseInt(hex.slice(2, 4), 16),
    b: Number.parseInt(hex.slice(4, 6), 16),
  }
}

function rgbToHsl({ r, g, b }: { r: number; g: number; b: number }): HslColor {
  const red = r / 255
  const green = g / 255
  const blue = b / 255
  const max = Math.max(red, green, blue)
  const min = Math.min(red, green, blue)
  const lightness = (max + min) / 2

  if (max === min) {
    return { h: 0, s: 0, l: lightness * 100 }
  }

  const delta = max - min
  const saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min)
  const hue =
    max === red
      ? (green - blue) / delta + (green < blue ? 6 : 0)
      : max === green
        ? (blue - red) / delta + 2
        : (red - green) / delta + 4

  return {
    h: hue * 60,
    s: saturation * 100,
    l: lightness * 100,
  }
}

function hslToHex({ h, s, l }: HslColor): string {
  const hue = normalizeHue(h) / 360
  const saturation = Math.max(0, Math.min(100, s)) / 100
  const lightness = Math.max(0, Math.min(100, l)) / 100

  if (saturation === 0) {
    const value = Math.round(lightness * 255)
    return rgbToHex(value, value, value)
  }

  const q =
    lightness < 0.5
      ? lightness * (1 + saturation)
      : lightness + saturation - lightness * saturation
  const p = 2 * lightness - q
  const channel = (offset: number) => {
    let t = hue + offset
    if (t < 0) t += 1
    if (t > 1) t -= 1
    if (t < 1 / 6) return p + (q - p) * 6 * t
    if (t < 1 / 2) return q
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
    return p
  }

  return rgbToHex(
    Math.round(channel(1 / 3) * 255),
    Math.round(channel(0) * 255),
    Math.round(channel(-1 / 3) * 255),
  )
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b].map((value) => value.toString(16).padStart(2, '0')).join('')}`
}

function getTenantBrandColor(): string {
  const flat = flattenThemeColors(
    (CURRENT_CLIENT as { theme?: { colors?: Record<string, unknown> } }).theme?.colors,
  )

  return flat?.primary?.trim() || IDENTICON_FALLBACK_BRAND_COLOR
}

export function generateIdenticonPalette(brandColor: string): string[] {
  const parsedBrandColor = parseHexColor(brandColor) ?? parseHexColor(IDENTICON_FALLBACK_BRAND_COLOR)
  if (!parsedBrandColor) return [IDENTICON_FALLBACK_BRAND_COLOR]

  const { h } = rgbToHsl(parsedBrandColor)
  const complementHue = normalizeHue(h + 180)
  const analogousHue = normalizeHue(h + 30)

  return [
    hslToHex({ h, s: 52, l: 76 }),
    hslToHex({ h: complementHue, s: 66, l: 70 }),
    hslToHex({ h: analogousHue, s: 48, l: 72 }),
    hslToHex({ h: complementHue, s: 22, l: 60 }),
    brandColor,
  ]
}

export function getIdenticonPalette(): string[] {
  return generateIdenticonPalette(getTenantBrandColor())
}

/** Stable boring-avatars props from a user/profile seed and pixel size. */
export function getIdenticonProps(seed: string, size: number): IdenticonRenderProps {
  const normalizedSeed = seed.trim() || 'profile'
  return {
    name: normalizedSeed,
    variant: IDENTICON_VARIANT,
    colors: getIdenticonPalette(),
    square: false,
    size: Math.max(1, Math.round(size)),
    title: false,
  }
}
